import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  NgZone,
  Output,
  SimpleChanges,
} from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { ActivatedRoute } from "@angular/router";
import { LoadingDialogComponent } from "@cotecna/dialogs";
import {
  DeletedSectionResult,
  DuplicatedSectionResult,
  FieldDefinitionChangeResult,
  RenamedSectionResult,
  Section,
} from "@cotecna/dynamic-view-engine";
import { throwError } from "rxjs";
import { PendingDeleteField } from "src/app/inspection/model/pending-delete-field";
import { PendingDeleteSection } from "src/app/inspection/model/pending-delete-section";
import { PendingDuplicateNaturalSection } from "src/app/inspection/model/pending-duplicate-natural-section";
import { PendingRenameField } from "src/app/inspection/model/pending-rename-field";
import { PendingRenameSection } from "src/app/inspection/model/pending-rename-section";
import {
  PendingReorderSections,
  ReorderSections,
} from "src/app/inspection/model/pending-reorder-fields";
import { FormDefinitionService } from "src/app/inspection/services/form-definition.service";
import { InspectionFormService } from "src/app/inspection/services/inspection-form-service/inspection-form.service";
import {
  DynamicFormActionsBase,
  FormFieldChange,
} from "src/app/items/components/item-form/dynamic-form-actions-base";
import { DynamicFormService } from "src/app/shared/services/dynamic-form/dynamic-form.service";
import { UserRolePermissionsService } from "src/app/shared/services/user-role-permissions/user-role-permissions.service";

import { PendingDuplicateNaturalField } from "../../../inspection/model/pending-duplicate-natural-field";
import { InspectionDetailsService } from "../../../inspection/services/inspection-details.service";
import { ScopeOfWorkFieldChangeCommand } from "../../services/commands/scope-of-work-field-change.command";
import { ScopeOfWorkQuery } from "../../services/queries/scope-of-work.query";
import { ScopeOfWorkService } from "../../services/scope-of-work.service";

@Component({
  selector: "im-sow-form",
  templateUrl: "./sow-form.component.html",
  styleUrls: ["./sow-form.component.scss"],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class SowFormComponent extends DynamicFormActionsBase {
  public loading: boolean = false;

  private duplicatingSection = false;

  @Input()
  declare inspectionId: string;

  @Input()
  declare sections: any[];

  @Input()
  declare controls: any[];

  @Input()
  formValues: any[];

  @Output()
  loaded: EventEmitter<void> = new EventEmitter();

  constructor(
    activatedRoute: ActivatedRoute,
    public formDefinitionService: FormDefinitionService,
    public inspectionFormService: InspectionFormService,
    public inspectionDetailsService: InspectionDetailsService,
    public dynamicFormService: DynamicFormService,
    ngZone: NgZone,
    private scopeOfWorkService: ScopeOfWorkService,
    private userRolePermissionsService: UserRolePermissionsService,
    private dialog: MatDialog,
    private changeDetectorRef: ChangeDetectorRef,
  ) {
    super(
      activatedRoute,
      formDefinitionService,
      inspectionFormService,
      inspectionDetailsService,
      dynamicFormService,
      ngZone,
    );
  }

  async ngOnInit() {
    this.initScopeOfWork();
  }

  ngOnChanges(simpleChanges: SimpleChanges) {
    if (
      simpleChanges?.sections ||
      simpleChanges?.controls ||
      simpleChanges?.formValues
    ) {
      this.initScopeOfWork();
    }
  }

  ngOnDestroy() {
    this.unsubscribeToFormEvents();
    this.form = null;
    this.item = null;
  }

  creationMode(): boolean {
    return this.inspectionId === null;
  }

  async onFormFieldChanges(formFieldChanges: FormFieldChange[]) {
    for (const formFieldChange of formFieldChanges) {
      this.scopeOfWorkService.applyScopeOfWorkFieldChange(
        this.values,
        formFieldChange,
      );
      if (!this.creationMode()) {
        while (this.duplicatingSection) {
          await new Promise((resolve) => setTimeout(resolve, 500));
        }
        await this.scopeOfWorkService.invokeCommand(
          new ScopeOfWorkFieldChangeCommand(formFieldChange),
        );
      }
    }
  }

  async onSectionRenamed(result: RenamedSectionResult) {
    if (!this.creationMode()) {
      await this.formDefinitionService.renameSection({
        inspectionId: this.inspectionId,
        itemId: null,
        sectionId: result.sectionId,
        name: result.newName,
      } as PendingRenameSection);
    }
  }

  private async onSectionsReordered(result: ReorderSections) {
    try {
      const pendingReorderSections = {
        inspectionId: this.inspectionId,
        itemId: null,
        sections: result.sections,
        sectionParentId: result.sectionParentId,
      } as PendingReorderSections;

      await this.formDefinitionService.reorderSections(pendingReorderSections);
    } catch (err) {
      console.error("on section reordered", err);
      throw err;
    }
  }

  async onSectionDuplicated(result: DuplicatedSectionResult) {
    if (!this.creationMode()) {
      const loadingDialogRef = this.dialog.open(LoadingDialogComponent, {
        autoFocus: false,
        disableClose: true,
        data: "Duplicating Section",
      });
      this.duplicatingSection = true;

      await this.formDefinitionService.duplicateNaturalSection({
        inspectionId: this.inspectionId,
        itemId: null,
        duplicatedSection: result.sectionDuplicated,
        duplicatedControls: result.duplicatedControls,
      } as PendingDuplicateNaturalSection);

      this.duplicatingSection = false;
      loadingDialogRef.close();

      await this.reorderChildSections(
        result.sectionDuplicated.duplicated.parentId,
        result.currentSections,
      );
    }
  }

  async onSectionDeleted(result: DeletedSectionResult) {
    if (!this.creationMode()) {
      await this.formDefinitionService.deleteSection({
        inspectionId: this.inspectionId,
        itemId: null,
        sectionId: result.sectionRemoved.id,
      } as PendingDeleteSection);

      await this.reorderChildSections(
        result.sectionRemoved.duplicated.parentId,
        result.currentSections,
      );
    }
  }

  private async reorderChildSections(parentId: string, sections: Section[]) {
    const sectionsToReorder: ReorderSections = {
      sectionParentId: parentId,
      sections: [],
    };

    sections.forEach((section) => {
      if (section.duplicated?.parentId === parentId) {
        sectionsToReorder.sections.push({
          sectionId: section.id,
          order: section.order,
        });
      }
    });

    if (sectionsToReorder.sections.length > 0) {
      await this.onSectionsReordered(sectionsToReorder);
    }
  }

  onFieldRenamed(result: FieldDefinitionChangeResult) {
    for (let i = 0; i < result.changedFieldsSections.length; i++) {
      if (!this.creationMode()) {
        this.formDefinitionService.renameField({
          inspectionId: this.inspectionId,
          itemId: null,
          sectionId: result.changedFieldsSections[i].id,
          fieldId: result.changedFields[i].id,
          name: result.changedFields[i].label,
        } as PendingRenameField);
      }
    }
  }

  onFieldDuplicated(result: FieldDefinitionChangeResult) {
    for (let i = 0; i < result.changedFieldsSections.length; i++) {
      if (!this.creationMode()) {
        this.formDefinitionService.duplicateNaturalField({
          inspectionId: this.inspectionId,
          itemId: null,
          sectionId: result.changedFieldsSections[i].id,
          duplicatedField: result.changedFields[i],
          duplicatedControl: result.changedControls[i],
        } as PendingDuplicateNaturalField);
      }
    }
  }

  onFieldDeleted(result: FieldDefinitionChangeResult) {
    for (let i = 0; i < result.changedFieldsSections.length; i++) {
      if (!this.creationMode()) {
        this.formDefinitionService.deleteField({
          inspectionId: this.inspectionId,
          itemId: null,
          sectionId: result.changedFieldsSections[i].id,
          fieldId: result.changedFields[i].id,
        } as PendingDeleteField);
      }
    }
  }

  onFullPictureRequested(fieldId: string) {
    if (!this.creationMode()) {
      this.getFullPictureSubscription = this.inspectionFormService
        .onFullPictureRequested(this.inspectionId, "", fieldId)
        .subscribe(
          (result) => {
            if (result) this.openPictureTab(result);
            this.getFullPictureSubscription.unsubscribe();
          },
          (err) => throwError(err),
        );
    }
  }

  private async initScopeOfWork(): Promise<void> {
    this.loading = true;
    this.isReadOnly = this.userRolePermissionsService.isReadOnly();
    this.setReadonlyState();
    this.unsubscribeToFormEvents();

    if (!this.creationMode()) {
      if (!this.isReadOnly) {
        this.subscribeToInspectionCommonData();
      }
      this.subscribeToFormData();
      this.scopeOfWorkService.invokeQuery(
        new ScopeOfWorkQuery(this.inspectionId),
      );
    } else {
      await this.initForm(this.sections, this.controls, this.formValues);
      this.loading = false;
      this.changeDetectorRef.detectChanges();
    }
  }

  private subscribeToFormData() {
    this.formDataSubscription =
      this.scopeOfWorkService.scopeOfWorkFormData.subscribe(
        async (formData) => {
          await this.initForm(
            formData?.form?.sections,
            formData?.form?.controls,
            formData?.values,
          );
          this.loading = false;
          this.changeDetectorRef.detectChanges();
          this.loaded.emit();
        },
      );
  }
}
